/**
 * Messages Component - Renders Steps with Mode Switching
 *
 * Intelligently renders steps based on debug mode:
 * - DEBUG mode: Full technical details, all tool calls, system prompts
 * - USER mode: Explanations only, thinking spinner, expandable details
 *
 * Memoized Step components prevent unnecessary re-renders of unchanged messages
 * when new messages are added
 *
 * Uses the reactive GraphStore pattern:
 * - useNodes(step.context_items) for reactive context items
 * - No manual DAG reconstruction needed
 */

import React from 'react'

import ThreadContext from '@sourcepilot/ui/framework/ui/molecules/thread-context'
import { Step } from '@sourcepilot/generated/chat/api/step';
import { ChatRoleType } from '@sourcepilot/generated/shared/enums';
import { useNodes } from '@sourcepilot/framework/relationship-manager';
import { useAtom } from 'jotai';
import { debugModeAtom } from '@sourcepilot/ui/shell/state/ui-atoms';
import { StepDebug } from '../organisms/step-debug';
import { StepUser } from '../organisms/step-user';

interface MessagesProps {
  messages: Step[];
}

export function Messages({ messages }: MessagesProps) {
  // Get current debug mode setting
  const [debugMode] = useAtom(debugModeAtom);

  return (
    <>
      {messages.map((message: Step, index: number) => (
        <React.Fragment key={message.id || `pending-${index}`}>
          {/* context - reactive access */}
          <MessageContext step={message} />

          {/* Render appropriate step component based on debug mode */}
          {debugMode ? (
            <StepDebug step={message} />
          ) : (
            <StepUser
              step={message}
              isLastStep={index === messages.length - 1}
            />
          )}
        </React.Fragment>
      ))}
    </>
  )
}

/**
 * Helper component to reactively access context items for a step
 */
function MessageContext({ step }: { step: Step }) {
  // Hook is always called unconditionally (satisfies React Rules of Hooks)
  const contextItems = useNodes(step.context_items).value();
  return <ThreadContext contextItems={contextItems} />;
}
